import { useId, type CSSProperties } from "react";

import type { ExtractionRequest, ExtractionStage, ExtractionStatus } from "@/api";
import { CheckIcon } from "@/components/icons";

import styles from "./ReadingJobScreen.module.css";

export interface ReadingStep {
  word: string;
  hint: string;
  stages: readonly ExtractionStage[];
}

/**
 * The three steps a person is told about while a sheet is read (plan 4 §2,
 * step 2). The worker's stages fold into them: turning the sheet belongs to
 * reading the outline, the walls' figures are the second step, and the check
 * — ring and scale, deterministic, a second at most — is the third. It shows
 * as open until the answer is in; the screen leaves the moment it is.
 */
export const READING_STEPS: readonly ReadingStep[] = [
  { word: "Umriss lesen", hint: "drei Lesungen der Form", stages: ["orient", "contour"] },
  { word: "Maße lesen", hint: "drei Lesungen je Wand", stages: ["extract"] },
  { word: "Prüfen", hint: "Umlauf und Maßstab", stages: ["objects"] },
];

/**
 * How long a whole reading takes, said before it starts and while it runs
 * (Welle 1, idea 24). The first real sheets read in 24–66 s each (batch E12,
 * case register §3.6 D, 13.10.2026); „etwa 1–2 Minuten" announced up to twice
 * what a person then waited. „meist", because the odd sheet runs past a
 * minute — from `LONGER_THAN_USUAL_S` on the screen says so.
 */
export const READING_DURATION = "Dauert meist 30–60 Sekunden.";

/**
 * The steps of one job. After a person chose a reading of the form (plan 4
 * §9, `request.kind` `choose_form`) the outline is theirs: only „Maße lesen ·
 * Prüfen“ run — also after a reload, which is why the job says so itself.
 */
export function stepsFor(request: ExtractionRequest | null | undefined): readonly ReadingStep[] {
  return request?.kind === "choose_form" ? READING_STEPS.slice(1) : READING_STEPS;
}

/** `null` status: nothing started yet — every step still to come. */
export function activeStep(
  stage: ExtractionStage | null,
  status: ExtractionStatus | null,
  steps: readonly ReadingStep[] = READING_STEPS,
): number {
  if (status === null || status === "queued") return -1;
  if (status === "succeeded") return steps.length;
  const index = steps.findIndex((step) => stage !== null && step.stages.includes(stage));
  return index === -1 ? 0 : index;
}

/**
 * The running step carries a strip of mesh — the picture mark's own
 * parallelograms, 22° off vertical, in the neutral grey — moving the way a
 * grating runs past. Under reduced motion it stands still; it is decoration,
 * the word beside it says what is happening.
 */
function MeshStrip() {
  // `url(#…)` must not meet the colons or guillemets React puts in an id.
  const id = `mesh${useId().replace(/[^a-zA-Z0-9_-]/g, "")}`;
  return (
    <span className={styles.meshBox} aria-hidden="true">
      <svg className={styles.mesh} focusable="false">
        <defs>
          <pattern id={id} width="14" height="10" patternUnits="userSpaceOnUse" patternTransform="skewX(-22)">
            <rect width="6" height="10" />
          </pattern>
        </defs>
        <rect width="100%" height="10" fill={`url(#${id})`} />
      </svg>
    </span>
  );
}

/**
 * Words, not a ring and not a percentage (CI §6) — the steps, the running
 * one marked current. Before a reading is started (`status` `null`) the
 * steps say what each will do, so the start shows what is about to happen
 * and turns into the progress in place.
 */
export function ReadingSteps({
  stage,
  status,
  request = null,
}: {
  stage: ExtractionStage | null;
  status: ExtractionStatus | null;
  request?: ExtractionRequest | null;
}) {
  const steps = stepsFor(request);
  const active = activeStep(stage, status, steps);
  return (
    <ol className={styles.steps} aria-label="Schritte" style={{ "--steps": steps.length } as CSSProperties}>
      {steps.map((step, index) => {
        const state = active === -1 ? "open" : index < active ? "done" : index === active ? "running" : "open";
        return (
          <li key={step.word} className={styles.step} data-state={state} aria-current={state === "running" ? "step" : undefined}>
            <span className={styles.stepMark} aria-hidden="true">
              {state === "done" ? <CheckIcon size={12} /> : index + 1}
            </span>
            <span className={styles.stepBody}>
              <span className={styles.stepWord}>{step.word}</span>
              <span className={styles.stepHint}>
                {state === "done" ? "fertig" : state === "running" || status === null ? step.hint : "gleich"}
              </span>
              {state === "running" ? <MeshStrip /> : null}
            </span>
          </li>
        );
      })}
    </ol>
  );
}
